// WidgetAnalyticsDashboard — admin /channels/widgets/analytics (S8 / CHG-2459 FE).
//
// Hand-built Widget Studio analytics surface composing the existing dashboard-v2
// primitives (DashboardPage shell + StatStrip KPIs + DataTable grouped tables),
// consuming the analytics report sextet on widgets-admin.ts. Mirrors the
// AnalyticsDashboard.tsx (CHG-1659) hand-roll precedent.
//
// Spec: specs/_product/widget-studio-ui.md §4 (analytics reports are read-only;
// the sextet shares one WidgetAnalyticsReportFilter). SEC-5: every money value
// shown here is the server-trusted rollup/fold total — never recomputed client
// side. CHG-2499: currency is driven OFF the response (summary.currency + each
// row's counters.currency = the org default; attribution prefers the trusted
// donation currency), formatted via formatCurrency(minorUnits, currency) — no
// hardcoded 'USD'. Composition-lint clean (no hardcoded color/currency;
// useFormatters; no raw date inputs — window is a <select>; DashboardPage owns
// page padding).

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardPage } from "../components/composition/DashboardPage";
import { StatStrip, type StatStripItem } from "../components/composition/StatStrip";
import { DataTable, type DataTableColumn } from "../components/composition/DataTable";
import { useFormatters } from "../contexts/IntlContext";
import {
  widgetsAdminApi,
  type WidgetAnalyticsByContentRow,
  type WidgetAnalyticsByInstallationRow,
  type WidgetAnalyticsByPageRow,
  type WidgetAnalyticsCounterSet,
  type WidgetAnalyticsFunnelResponse,
  type WidgetAnalyticsReportFilter,
  type WidgetFamily,
  type WidgetFunnelType,
} from "../api/widgets-admin";

const TABLE_LIMIT = 25;

const WINDOWS: Array<{ value: string; label: string; days: number | null }> = [
  { value: "7", label: "Last 7 days", days: 7 },
  { value: "30", label: "Last 30 days", days: 30 },
  { value: "90", label: "Last 90 days", days: 90 },
  { value: "all", label: "All time", days: null },
];

const FUNNEL_TYPES: Array<{ value: WidgetFunnelType; label: string }> = [
  { value: "donation", label: "Donation" },
  { value: "event_registration", label: "Event registration" },
  { value: "announcement", label: "Announcement" },
  { value: "social_share", label: "Social share" },
];

const FAMILIES: Array<{ value: "" | WidgetFamily; label: string }> = [
  { value: "", label: "All families" },
  { value: "donation", label: "Donation" },
  { value: "event", label: "Event" },
  { value: "announcement", label: "Announcement" },
  { value: "social_post", label: "Social post" },
];

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

// Flatten a grouped row's nested counter set onto the row so DataTable's flat
// `row[field]` access (and its currency formatter) can address each measure.
// CHG-2499: `currency` rides on the counter set (the org default the server
// stamped on every row), so the spread carries it onto the flat row — the
// DataTable currency column reads `currencyField: "currency"` off it. No
// hardcoded 'USD'.
type FlatRow = Record<string, unknown> & { id: string; currency: string };

function flatten(
  dims: Record<string, string | null>,
  counters: WidgetAnalyticsCounterSet,
  id: string,
): FlatRow {
  return { id, ...dims, ...counters };
}

function counterColumns<T extends FlatRow>(): DataTableColumn<T>[] {
  return [
    { field: "impressions", label: "Impressions", type: "number", sortable: false },
    { field: "opens", label: "Opens", type: "number" },
    { field: "cta_clicks", label: "CTA clicks", type: "number" },
    { field: "donations_completed", label: "Donations", type: "number" },
    {
      field: "donation_gross_minor_units",
      label: "Gross",
      type: "currency",
      currencyField: "currency",
    },
  ];
}

function downloadCsv(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function FilterBar({
  funnelType,
  family,
  windowValue,
  onFunnelType,
  onFamily,
  onWindow,
}: {
  funnelType: WidgetFunnelType;
  family: "" | WidgetFamily;
  windowValue: string;
  onFunnelType: (v: WidgetFunnelType) => void;
  onFamily: (v: "" | WidgetFamily) => void;
  onWindow: (v: string) => void;
}) {
  return (
    <section
      className="card"
      aria-label="Analytics filters"
      style={{ padding: 12, marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap" }}
    >
      <Field label="Funnel">
        <select
          aria-label="Funnel type"
          className="input"
          value={funnelType}
          onChange={(e) => onFunnelType(e.target.value as WidgetFunnelType)}
        >
          {FUNNEL_TYPES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Family">
        <select
          aria-label="Widget family"
          className="input"
          value={family}
          onChange={(e) => onFamily(e.target.value as "" | WidgetFamily)}
        >
          {FAMILIES.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Window">
        <select
          aria-label="Time window"
          className="input"
          value={windowValue}
          onChange={(e) => onWindow(e.target.value)}
        >
          {WINDOWS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
      <span style={{ color: "var(--color-text-muted)", fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}

function FunnelView({
  data,
  loading,
}: {
  data: WidgetAnalyticsFunnelResponse | undefined;
  loading: boolean;
}) {
  const fmt = useFormatters();
  const steps = data?.steps ?? [];
  const top = steps[0]?.count ?? 0;
  return (
    <section
      className="card"
      aria-labelledby="widget-funnel-title"
      style={{ padding: 16, marginBottom: 16 }}
    >
      <h2
        id="widget-funnel-title"
        style={{
          fontSize: 12,
          fontWeight: 500,
          margin: "0 0 12px",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Conversion funnel
      </h2>
      {loading ? (
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>Loading…</p>
      ) : steps.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
          No funnel activity for this window.
        </p>
      ) : (
        <div data-widget-funnel>
          {steps.map((step, i) => {
            const widthPct = top === 0 ? 0 : Math.max(6, (step.count / top) * 100);
            return (
              <div key={step.step} data-funnel-step={step.step} style={{ marginBottom: i === steps.length - 1 ? 0 : 10 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 500, color: "var(--color-text)" }}>{step.label}</span>
                  <span style={{ color: "var(--color-text-muted)" }}>
                    {fmt.formatNumber(step.count)}
                    {step.rate_from_prev != null ? ` · ${fmt.formatPercent(step.rate_from_prev)}` : ""}
                  </span>
                </div>
                <div
                  style={{
                    height: 12,
                    width: `${widthPct}%`,
                    minWidth: step.count > 0 ? 6 : 0,
                    background: "var(--color-primary)",
                    opacity: Math.max(0.4, 0.9 - i * 0.16),
                    borderRadius: 3,
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TableSection<T extends FlatRow>({
  title,
  rows,
  columns,
  loading,
  error,
  total,
}: {
  title: string;
  rows: T[];
  columns: DataTableColumn<T>[];
  loading: boolean;
  error: Error | null;
  total: number;
}) {
  return (
    <section aria-label={title} style={{ marginBottom: 20 }}>
      <h2
        style={{
          fontSize: 12,
          fontWeight: 500,
          margin: "0 0 8px",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {title} {total > 0 ? `(${total})` : ""}
      </h2>
      <DataTable
        data={rows}
        columns={columns}
        isLoading={loading}
        error={error}
        emptyState={{ title: "No data", subtitle: "No usage rolled up for this filter yet." }}
      />
    </section>
  );
}

export function WidgetAnalyticsDashboard() {
  const [funnelType, setFunnelType] = useState<WidgetFunnelType>("donation");
  const [family, setFamily] = useState<"" | WidgetFamily>("");
  const [windowValue, setWindowValue] = useState<string>("30");
  const [exporting, setExporting] = useState(false);

  const filter: WidgetAnalyticsReportFilter = useMemo(() => {
    const win = WINDOWS.find((w) => w.value === windowValue);
    return {
      from: win?.days ? daysAgoIso(win.days) : undefined,
      family: family || undefined,
      limit: TABLE_LIMIT,
    };
  }, [windowValue, family]);

  // Stable cache key for the active filter (the report queries all re-run when
  // any filter dimension changes).
  const fk = `${filter.from ?? "all"}|${filter.family ?? "*"}`;

  const summary = useQuery({
    queryKey: ["widget-analytics", "summary"] as const,
    queryFn: () => widgetsAdminApi.analyticsSummary(),
  });
  const funnel = useQuery({
    queryKey: ["widget-analytics", "funnel", funnelType, fk] as const,
    queryFn: () => widgetsAdminApi.analyticsFunnel(funnelType, filter),
  });
  const byPage = useQuery({
    queryKey: ["widget-analytics", "by-page", fk] as const,
    queryFn: () => widgetsAdminApi.analyticsByPage(filter),
  });
  const byInstallation = useQuery({
    queryKey: ["widget-analytics", "by-installation", fk] as const,
    queryFn: () => widgetsAdminApi.analyticsByInstallation(filter),
  });
  const byContent = useQuery({
    queryKey: ["widget-analytics", "by-content", fk] as const,
    queryFn: () => widgetsAdminApi.analyticsByContent(filter),
  });

  const statItems: StatStripItem[] = useMemo(() => {
    const s = summary.data;
    return [
      { id: "impressions", label: "Impressions", value: s?.impressions ?? null, format: "number", tone: "g-channels" },
      { id: "viewable", label: "Viewable", value: s?.viewable_impressions ?? null, format: "number", tone: "g-channels" },
      { id: "opens", label: "Opens", value: s?.opens ?? null, format: "number", tone: "g-channels" },
      { id: "clicks", label: "CTA clicks", value: s?.cta_clicks ?? null, format: "number", tone: "g-channels" },
      { id: "donations", label: "Donations", value: s?.donations_completed ?? null, format: "number", tone: "g-giving" },
      {
        id: "gross",
        label: "Gross raised",
        value: s?.donation_gross_minor_units ?? null,
        format: "currency",
        // CHG-2499 — org default currency from the summary response, not hardcoded.
        currency: s?.currency,
        tone: "g-giving",
      },
    ];
  }, [summary.data]);

  const pageRows: FlatRow[] = (byPage.data?.rows ?? []).map((r: WidgetAnalyticsByPageRow, i) =>
    flatten({ domain: r.domain, url_pattern: r.url_pattern }, r.counters, `pg-${i}`),
  );
  const installRows: FlatRow[] = (byInstallation.data?.rows ?? []).map(
    (r: WidgetAnalyticsByInstallationRow, i) =>
      flatten(
        { widget_instance_id: r.widget_instance_id, domain: r.domain, surface: r.surface, placement: r.placement },
        r.counters,
        `in-${i}`,
      ),
  );
  const contentRows: FlatRow[] = (byContent.data?.rows ?? []).map(
    (r: WidgetAnalyticsByContentRow, i) =>
      flatten(
        {
          content_binding_entity_type: r.content_binding_entity_type,
          content_binding_entity_id: r.content_binding_entity_id,
          campaign_id: r.campaign_id,
          event_id_ref: r.event_id_ref,
        },
        r.counters,
        `ct-${i}`,
      ),
  );

  async function handleExport() {
    setExporting(true);
    try {
      const blob = await widgetsAdminApi.analyticsExportCsv(filter);
      downloadCsv(blob, "widget-analytics.csv");
    } finally {
      setExporting(false);
    }
  }

  const pageCols: DataTableColumn<FlatRow>[] = [
    { field: "domain", label: "Domain", type: "text" },
    { field: "url_pattern", label: "URL pattern", type: "text" },
    ...counterColumns<FlatRow>(),
  ];
  const installCols: DataTableColumn<FlatRow>[] = [
    { field: "widget_instance_id", label: "Instance", type: "text" },
    { field: "domain", label: "Domain", type: "text" },
    { field: "surface", label: "Surface", type: "text" },
    { field: "placement", label: "Placement", type: "text" },
    ...counterColumns<FlatRow>(),
  ];
  const contentCols: DataTableColumn<FlatRow>[] = [
    { field: "content_binding_entity_type", label: "Type", type: "text" },
    { field: "content_binding_entity_id", label: "Content id", type: "text" },
    { field: "campaign_id", label: "Campaign", type: "text" },
    ...counterColumns<FlatRow>(),
  ];

  return (
    <DashboardPage
      title="Widget analytics"
      subtitle="Usage funnel and grouped metrics across your embedded widgets."
      headerActions={[
        {
          id: "export",
          label: exporting ? "Exporting…" : "Export CSV",
          variant: "secondary",
          disabled: exporting,
          onClick: handleExport,
        },
      ]}
      stats={<StatStrip items={statItems} loading={summary.isLoading} />}
      loading={summary.isLoading}
      error={(summary.error as Error | null) ?? null}
      onRetry={() => summary.refetch()}
      callout={null}
      feed={
        <div>
          <FilterBar
            funnelType={funnelType}
            family={family}
            windowValue={windowValue}
            onFunnelType={setFunnelType}
            onFamily={setFamily}
            onWindow={setWindowValue}
          />
          <FunnelView data={funnel.data} loading={funnel.isLoading} />
          <TableSection
            title="By page"
            rows={pageRows}
            columns={pageCols}
            loading={byPage.isLoading}
            error={(byPage.error as Error | null) ?? null}
            total={byPage.data?.total ?? 0}
          />
          <TableSection
            title="By installation"
            rows={installRows}
            columns={installCols}
            loading={byInstallation.isLoading}
            error={(byInstallation.error as Error | null) ?? null}
            total={byInstallation.data?.total ?? 0}
          />
          <TableSection
            title="By content"
            rows={contentRows}
            columns={contentCols}
            loading={byContent.isLoading}
            error={(byContent.error as Error | null) ?? null}
            total={byContent.data?.total ?? 0}
          />
        </div>
      }
    />
  );
}
